import React, { useCallback, useEffect, useState } from 'react';
import {
    AutocompleteInput,
    Create,
    DateInput,
    NumberInput,
    ReferenceInput,
    SelectInput,
    SimpleForm,
    TextInput,
    maxLength,
    minValue,
    required,
    useNotify,
} from 'react-admin';
import { useFormContext, useWatch } from 'react-hook-form';
import { Alert } from '@mui/material';

const KINDS = [
    { id: 'voice', name: 'Voz' },
    { id: 'text', name: 'Texto' },
];

const UNITS = [
    { id: 'minutes', name: 'Minutos' },
    { id: 'interviews', name: 'Nº de entrevistas' },
    { id: 'tokens', name: 'Tokens' },
];

/**
 * El limite lo pone la COLUMNA, no el diseño: `ai_agent_pack.notes` es un `varchar(255)`.
 *
 * Y hay que respetarlo desde aqui porque el back no avisa si se pasa: MySQL de produccion corre
 * con `sql_mode` vacio —sin `STRICT_TRANS_TABLES`—, asi que una nota mas larga **se trunca en
 * silencio**. El alta devuelve 201, CS ve que ha ido bien, y la nota que justifica el bono queda
 * cortada a media frase. No es hipotetico: las notas reales son el texto comercial del paquete y
 * ya rozan el limite.
 */
const NOTES_MAX_LENGTH = 255;

/**
 * Limpia el formulario cuando el alta ha ido bien, y **solo** entonces.
 *
 * Va aqui dentro y no en el `onSuccess` del `<Create>` porque `reset()` necesita el contexto del
 * formulario, que solo existe por debajo del `<SimpleForm>`. El patron —un componente que no
 * pinta nada y solo toca el formulario— es el mismo que `AutoSlug` en el alta de campos
 * personalizados.
 *
 * Si el alta falla no se limpia nada, a proposito: CS corrige lo que sea y reintenta sin volver a
 * teclear el bono entero.
 *
 * Efecto de propina, y es el que se buscaba: al quedar el formulario limpio queda tambien
 * *pristine*, y el `<SaveButton>` de react-admin se deshabilita cuando `!isDirty`. Asi el boton
 * no vuelve a estar disponible hasta que se escriba algo distinto, que es justo lo que evita el
 * segundo alta accidental con los mismos datos.
 */
const ResetOnSuccess = ({ signal }) => {
    const { reset } = useFormContext();

    useEffect(() => {
        if (signal > 0) {
            reset();
        }
    }, [signal, reset]);

    return null;
};

/**
 * Notas internas, con el contador pegado al limite real de la columna.
 *
 * Dos barreras y no una: `inputProps.maxLength` es el tope duro del `<textarea>` —corta tambien
 * al pegar—, y el validador cubre el caso de que el valor llegue por otra via. El contador esta
 * porque un tope duro sin explicar se siente como que el teclado ha dejado de responder.
 */
const NotesInput = () => {
    const notes = useWatch({ name: 'notes' }) ?? '';

    return (
        <TextInput
            source="notes"
            label="Notas internas"
            fullWidth
            multiline
            inputProps={{ maxLength: NOTES_MAX_LENGTH }}
            validate={maxLength(NOTES_MAX_LENGTH)}
            helperText={`${notes.length} / ${NOTES_MAX_LENGTH} caracteres`}
        />
    );
};

/**
 * Alta y renovacion de un bono (86cbaa21u). **Es la misma operacion**: no hay pantalla de editar.
 *
 * El aviso de arriba describe lo que el back hace de verdad, que no es lo que parece desde aqui
 * (12466mx3wuz). Guardar **no cierra** el bono que esta activo: `AiAgentPackService::purchase()`
 * nace el nuevo en `provisioned` y lo mete en una cola FIFO por `(purchased_at, id)`, y solo lo
 * nace `active` si el cliente no tenia ninguno de ese tipo. El relevo lo dispara el AGOTAMIENTO
 * del activo, no el alta.
 *
 * La unica excepcion es comprar sobre un bono ya excedido (D-ARQ-11): ahi el relevo ocurre en el
 * acto, pero el testigo va al primero de la cola —`findFirstProvisioned()`—, que por FIFO puede
 * no ser el que se acaba de dar de alta.
 *
 * Y la fecha ya no se rechaza: se retiraron los 409 por fecha duplicada y por fecha anterior a
 * otro bono, porque sin periodos que solapar no hay nada que colisionar. Lo unico que cambia una
 * fecha pasada es el TURNO en la cola, y eso si hay que decirlo: adelanta al bono al que le
 * tocaba entrar.
 *
 * **Sobre el doble clic.** El boton de guardar no necesita un candado propio: el `<SaveButton>`
 * de react-admin se deshabilita solo mientras `isSubmitting`, y ese estado dura toda la peticion
 * porque el `save()` del controlador devuelve una promesa que no resuelve hasta que la mutacion
 * termina. Al resolverse se libera: si el alta fallo, el formulario conserva los datos y el boton
 * vuelve a estar activo para reintentar; si fue bien, `ResetOnSuccess` lo deja limpio y el boton
 * sigue bloqueado por `!isDirty` hasta que se escriba algo nuevo. **No le pongas un `disabled`
 * ni un `alwaysEnable` encima**: lo unico que se conseguiria es desincronizarlo de la peticion.
 *
 * `exhausted_at` y `closed_at` no aparecen en el formulario a proposito: los sella el sistema, y
 * si se pudieran teclear la factura dejaria de ser defendible.
 */
const AiAgentPackCreate = () => {
    const notify = useNotify();
    const [savedCount, setSavedCount] = useState(0);

    /*
        Sobrescribir `onSuccess` sustituye los efectos por defecto del controlador, aviso
        incluido, asi que hay que volver a notificar a mano. Se usa la misma clave que react-admin
        para que el mensaje siga siendo el que CS ve en el resto del panel.
    */
    const onSuccess = useCallback(() => {
        notify('ra.notification.created', { type: 'info', messageArgs: { smart_count: 1 } });
        setSavedCount((count) => count + 1);
    }, [notify]);

    return (
        <Create redirect={false} mutationOptions={{ onSuccess }}>
            <SimpleForm>
                <ResetOnSuccess signal={savedCount} />

                <Alert severity="info" sx={{ mb: 2, width: '100%' }}>
                    Renovar es crear un bono nuevo, nunca editar el anterior. Pero{' '}
                    <strong>dar de alta un bono no cierra el que está activo</strong>: el nuevo
                    entra en cola y solo nace activo si el cliente no tenía ninguno de ese tipo.
                    <br />
                    Arranca cuando <strong>toma el relevo</strong>, el día que el activo se agota —
                    no en la fecha de compra. Si el activo ya venía excedido, el relevo es
                    inmediato, pero entra el primero de la cola, que puede no ser este.
                    <br />
                    La fecha de compra puede ser pasada —es la fecha en que el cliente paga— y{' '}
                    <strong>el back no la rechaza</strong>: lo único que decide es el turno en la
                    cola, y una fecha anterior adelanta este bono a los que ya estaban esperando.
                </Alert>

                <ReferenceInput
                    source="client_id"
                    reference="clients"
                    label="Cliente"
                    sort={{ field: 'name', order: 'ASC' }}
                    perPage={1000}
                >
                    <AutocompleteInput
                        optionText="name"
                        validate={required()}
                        filterToQuery={(searchText) => ({ name: searchText })}
                    />
                </ReferenceInput>

                <SelectInput source="kind" label="Tipo" choices={KINDS} validate={required()} />

                {/*
                    La unidad es una propiedad del bono y no se puede cambiar despues: es lo unico
                    que decide que medida se compara con las unidades incluidas.
                */}
                <SelectInput source="unit" label="Unidad" choices={UNITS} validate={required()} />

                <NumberInput
                    source="included_units"
                    label="Unidades incluidas"
                    validate={[required(), minValue(0)]}
                />

                {/*
                    La fecha de COMPRA. No decide cuando arranca el bono: eso lo hace el sistema el
                    dia que empieza a consumirse. Aqui solo ordena la cola —el primero en comprarse
                    es el primero en consumirse— y le sirve a Administracion para saber cuando se
                    vendio.
                */}
                <DateInput
                    source="purchased_at"
                    label="Fecha de compra (cuando paga)"
                    validate={required()}
                />

                <NotesInput />
            </SimpleForm>
        </Create>
    );
};

export default AiAgentPackCreate;
